import loadable from '@loadable/component';
import React from 'react';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import Loading from './common/Loading';
import FullscreenError from 'common/FullscreenError/FullscreenError';

import { useNavigationTracking } from 'common/Analytics/PageViewTracker';
import Layout from './common/Layout/Layout';
import { ViewerContext_UserFragment } from './common/Viewer/ViewerContext.generated';
import { Role } from '@99designs/frontend-components/apps/global-nav/consts';
import { useViewer } from './common/Viewer/ViewerContext';
import { Site } from '@99designs/frontend-components/apps/global-nav/lib/Site';
import NoSsr from 'common/Ssr/NoSsr';
import { WorkPageStatus } from 'linkTo';

const Messages = loadable(() => import('./pages/messages'));
const WebOnboarderPage = loadable(() => import('./pages/web/get-started/WebOnboarderPage'));
const CancelInvitesPage = loadable(() => import('./pages/projects/cancel-invites'));
const ProjectsApp = loadable(() => import('./pages/projects/ProjectsApp'));
const Login = loadable(() => import('./pages/auth/Login'));
const SignUp = loadable(() => import('./pages/auth/SignUp'));
const VerifyLogin = loadable(() => import('./pages/auth/VerifyTwoFactor/VerifyLogin'));
const VerifyAddPayout = loadable(() => import('./pages/auth/VerifyTwoFactor/VerifyAddPayout'));
const Notifications = loadable(() => import('./pages/notifications'));
const Status = loadable(() => import('./pages/status'));
const Account = loadable(() => import('./pages/account'));
const GettingStarted = loadable(() => import('./pages/getting-started'));
const HandoverMessages = loadable(() => import('./pages/handover/messages'));
const Create = loadable(() => import('./pages/create'));
const YourWorkApp = loadable(() => import('./pages/your-work/YourWorkApp'));
const DesignerOnboarderPage = loadable(
    () => import('./pages/designer-onboarding/DesignerOnboarderPage')
);
const AdminRedirects = loadable(() => import('./pages/admin-redirects'));

export default function Routes({ site }: { site: Site }) {
    const viewer = useViewer();
    useNavigationTracking();

    return (
        <Switch>
            <Route exact path="/login" render={() => <Login fallback={<Loading />} />} />
            <Route path="/admin-redirect/*" render={() => <AdminRedirects />} />
            <Route
                exact
                path="/signup/:variant?"
                render={(props: RouteComponentProps<{ variant?: 'designer' | 'invite' }>) => (
                    <SignUp fallback={<Loading />} variant={props.match.params.variant} />
                )}
            />
            <Route
                exact
                path="/auth/verify"
                render={() => <VerifyLogin fallback={<Loading />} />}
            />
            <Route
                path={['/briefs/*', '/squarespace/*']}
                render={() => (
                    <NoSsr fallback={<Loading />}>
                        <Create />
                    </NoSsr>
                )}
            />
            <Route path="/web/get-started" render={() => <WebOnboarderPage />} />
            <Route
                exact
                path="/work/:status(in-progress|all|completed|canceled)?"
                render={(props: RouteComponentProps<{ status?: WorkPageStatus }>) => (
                    <Layout role={primaryRole(viewer)} site={site} footerStyle="reduced">
                        <YourWorkApp
                            status={props.match.params.status}
                            language={site.getLanguageCode()}
                        />
                    </Layout>
                )}
            />
            <Route
                path="/account/getting-started"
                render={() => <DesignerOnboarderPage fallback={<Loading />} />}
            />
            <Route
                path={'/'}
                render={() => (
                    <Layout role={primaryRole(viewer)} site={site}>
                        <Switch>
                            <Route
                                path="/messages/:recipientId?"
                                render={(props: RouteComponentProps<{ recipientId: string }>) => (
                                    <Messages
                                        recipientId={props.match.params.recipientId}
                                        fallback={<Loading />}
                                        {...props}
                                    />
                                )}
                            />
                            <Route
                                path="/notifications"
                                render={(props: RouteComponentProps) => (
                                    <Notifications fallback={<Loading />} {...props} />
                                )}
                            />
                            <Route
                                path="/auth/verify-payout" //TODO add route or update bastion with wildcard?
                                render={(props: RouteComponentProps) => (
                                    <VerifyAddPayout fallback={<Loading />} {...props} />
                                )}
                            />
                            <Route
                                exact
                                path="/projects/:projectId/cancel-invites"
                                component={CancelInvitesPage}
                            />
                            <Route
                                // TODO: remove /spa together with <ProjectsRedirect />
                                path={['/spa/projects/:projectId', '/projects/:projectId']}
                                component={ProjectsApp}
                            />
                            <Route
                                exact
                                path="/spa/status"
                                render={() => <Status fallback={<Loading />} />}
                            />
                            <Route
                                exact
                                path="/account/application"
                                render={() => <GettingStarted fallback={<Loading />} />}
                            />
                            <Route
                                path="/account"
                                render={() => <Account fallback={<Loading />} />}
                            />
                            <Route
                                path={[
                                    '/spa/handover/:handoverId/messages',
                                    '/handover/:handoverId/messages',
                                ]}
                            >
                                <HandoverMessages />
                            </Route>
                            <Route render={() => <FullscreenError errorCode="404" />} />
                        </Switch>
                    </Layout>
                )}
            />
        </Switch>
    );
}

function primaryRole(user: ViewerContext_UserFragment | null): Role {
    if (!user) {
        return 'logged-out';
    } else if (user.isAdmin) {
        return 'contest-holder';
    } else if (user.isDesigner) {
        return 'designer';
    } else {
        return 'contest-holder';
    }
}
